import { ReactNode, useRef } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { ScrollControl, useScrollControls } from '@frontend/charts';
import { theme } from '@frontend/theme';

const styles = {
  container: css`
    width: fit-content;
    max-width: 100%;
  `,
  scrollControlsContainer: css`
    position: relative;
  `,
  scrollContainer: css`
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.small};
    overflow-x: auto;
    overflow-y: hidden;
    padding: ${theme.spacing(2, 0, 1.5)};

    &::-webkit-scrollbar {
      height: ${theme.spacing(1)};
    }

    &::-webkit-scrollbar-track {
      margin: ${theme.spacing(0, 4)};
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${theme.colors.neutral20};
      border-radius: ${theme.borderRadius.small};
    }
  `,
  summaryItems: css`
    display: inline-flex;
    min-width: max-content;
    padding-bottom: ${theme.spacing(0.5)};

    > * {
      min-width: 120px;
      padding: ${theme.spacing(0, 3)};
    }

    > *:not(:last-child) {
      border-right: 1px solid ${theme.colors.neutral20};
    }
  `,
  scrollControl: css`
    height: ${theme.spacing(4)};
    width: ${theme.spacing(4)};
    border-color: ${theme.colors.neutral10};

    &.left-scroll.absolute,
    &.left-scroll.absolute.small-mode {
      left: 0;
    }

    &.right-scroll.absolute,
    &.right-scroll.absolute.small-mode {
      right: 0;
    }
  `,
};

interface SummaryScrollContainerProps {
  children: ReactNode;
  style?: SerializedStyles;
}

export const SummaryScrollContainer = ({ children, style }: SummaryScrollContainerProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { isLeftScrollDisabled, isRightScrollDisabled, isScrollable, scrollLeft, scrollRight } = useScrollControls({
    containerRef: scrollContainerRef,
  });

  return (
    <div className='invoice-summary-card' css={[styles.container, style]}>
      <div className='invoice-summary-card__scroll-container' css={styles.scrollControlsContainer}>
        <ScrollControl
          absolute
          buttonStyles={styles.scrollControl}
          direction='left'
          isVisible={isScrollable && !isLeftScrollDisabled}
          onClick={scrollLeft}
          trackingId='pay-portal-invoices-btn-summary-scroll-left'
        />
        <div ref={scrollContainerRef} css={styles.scrollContainer}>
          <div css={styles.summaryItems}>{children}</div>
        </div>
        <ScrollControl
          absolute
          buttonStyles={styles.scrollControl}
          direction='right'
          isVisible={isScrollable && !isRightScrollDisabled}
          onClick={scrollRight}
          trackingId='pay-portal-invoices-btn-summary-scroll-right'
        />
      </div>
    </div>
  );
};
